import type { Engagement } from '../../lib/types';

const formatLabels = {
  in_person: 'In person',
  virtual: 'Virtual',
  hybrid: 'Hybrid'
} as const;

function engagementTime(engagement: Engagement, fallback: number) {
  return engagement.startsAt ? new Date(engagement.startsAt).getTime() : fallback;
}

function sortEngagements(engagements: Engagement[]) {
  return [...engagements].sort((left, right) => {
    const leftUpcoming = left.status === 'upcoming';
    const rightUpcoming = right.status === 'upcoming';

    if (leftUpcoming !== rightUpcoming) return leftUpcoming ? -1 : 1;

    if (leftUpcoming) {
      return engagementTime(left, Number.MAX_SAFE_INTEGER) - engagementTime(right, Number.MAX_SAFE_INTEGER)
        || left.sortOrder - right.sortOrder;
    }

    return engagementTime(right, Number.MIN_SAFE_INTEGER) - engagementTime(left, Number.MIN_SAFE_INTEGER)
      || left.sortOrder - right.sortOrder;
  });
}

export function EngagementList({ engagements }: { engagements: Engagement[] }) {
  if (engagements.length === 0) {
    return (
      <div className="empty-state">
        <p>New engagements are being scheduled. Bring this work to your audience.</p>
        <a className="text-link" href="/contact">Invite Christina to speak</a>
      </div>
    );
  }

  return (
    <div className="engagement-list">
      {sortEngagements(engagements).map((engagement) => (
        <article className="engagement-card" data-engagement-id={engagement.id} key={engagement.id}>
          <div className="engagement-card__date">
            <span>{engagement.dateLabel}</span>
            <small>{engagement.status === 'upcoming' ? 'Upcoming' : engagement.status}</small>
          </div>
          <div>
            <p className="eyebrow">{engagement.organization}</p>
            <h2>{engagement.title}</h2>
            {engagement.summary && <p>{engagement.summary}</p>}
          </div>
          <div className="engagement-card__meta">
            <span>{engagement.location}</span>
            <span>{formatLabels[engagement.format]}</span>
            {engagement.eventUrl && <a className="text-link" href={engagement.eventUrl}>Event details</a>}
          </div>
        </article>
      ))}
    </div>
  );
}
